/**
 * `<KeyboardHelp>` — the `?` keyboard-shortcut reference overlay (UI remaster U8).
 *
 * The shell's single-letter surface shortcuts (`<KeyboardShortcuts>`) and the ⌘K
 * palette are invisible UI — nothing in the chrome tells the user they exist. This
 * overlay lists them, grouped:
 *
 *   - **Surfaces** — read LIVE from the `ShellMount` nav registry (`NavItem.kbd`),
 *     the same source `<KeyboardShortcuts>` navigates from and `<NavSidebar>` renders
 *     chips from. Registering a new surface with a `kbd` automatically appears here.
 *   - **Global** — the chords that exist in code: ⌘K / Ctrl+K (CommandPalette's
 *     window handler), `?` (this overlay), Esc (close overlays).
 *
 * Editor-scoped keys (Ctrl+F find-in-note, Ctrl+S save) are deliberately NOT listed:
 * no static editor-shortcut list exists in the codebase to render from, and this
 * overlay must not invent one that could drift from the real handlers.
 *
 * Open/close:
 *   - `?` (Shift+/) opens — with the SAME guard rules `<KeyboardShortcuts>` uses:
 *     not from INPUT/TEXTAREA/SELECT, not from contentEditable, no Meta/Ctrl/Alt
 *     modifier (Shift is allowed — `?` requires it), CommandPalette closed.
 *   - Esc or a backdrop click closes; `?` toggles closed too.
 *   - A "Keyboard shortcuts" command is registered into the `ShellMount` palette
 *     registry (same pattern as `editorChatMount`'s static commands), so ⌘K →
 *     "Keyboard shortcuts" opens it discoverably.
 *
 * Mirrors `<CommandPalette>`'s backdrop idiom (fixed scrim, centered panel,
 * z-index 100, Esc/backdrop close). Renders `null` while closed.
 */

import { useEffect, useState } from 'react';
import { useUiStore } from '@/stores';
import { useShellMountSnapshot } from './ShellMountContext';
import './keyboardHelp.css';

/** One shortcut row — the key chips + what they do. */
interface ShortcutRow {
  keys: string[];
  label: string;
}

interface ShortcutGroup {
  title: string;
  rows: ShortcutRow[];
}

/** The global chords that exist in code (CommandPalette / this overlay / Esc). */
const GLOBAL_ROWS: ShortcutRow[] = [
  { keys: ['⌘K', 'Ctrl+K'], label: 'Open the command palette' },
  { keys: ['?'], label: 'Keyboard shortcuts (this overlay)' },
  { keys: ['Esc'], label: 'Close palette / overlays' },
];

export function KeyboardHelp(): JSX.Element | null {
  const mount = useShellMountSnapshot();
  const [open, setOpen] = useState(false);
  const paletteOpen = useUiStore((s) => s.commandPaletteOpen);

  // The global `?` open/toggle handler + Esc-to-close. Guards are verbatim from
  // `<KeyboardShortcuts>` (the known-good design): focused form fields and editable
  // surfaces keep their keystrokes; chords stay reserved; the palette wins when open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && open) {
        setOpen(false);
        return;
      }
      if (e.key !== '?') return;
      // Guard 3: Meta/Ctrl/Alt disqualify (Shift is allowed — `?` needs it).
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Guards 1 + 2: focused form fields / editable elements keep their keys.
      const target = e.target as HTMLElement | null;
      if (target !== null) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (target.isContentEditable) return;
      }
      // Guard 4: the CommandPalette owns the keyboard while it's open.
      if (paletteOpen) return;
      e.preventDefault();
      setOpen((o) => !o);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, paletteOpen]);

  // Register the palette command through the same ShellMount registry the other
  // static commands use (see editorChatMount) — discoverable via ⌘K.
  useEffect(() => {
    const unregister = mount.registerCommand({
      id: 'help.keyboardShortcuts',
      label: 'Keyboard shortcuts',
      group: 'Help',
      keywords: ['keyboard', 'shortcuts', 'keys', 'hotkeys', 'help', 'kbd'],
      run: () => setOpen(true),
    });
    return unregister;
  }, [mount]);

  if (!open) return null;

  // Surface-navigation keys from the LIVE registry — computed at render (cheap;
  // `useShellMountSnapshot` re-renders us if a registration changes while open).
  const surfaceRows: ShortcutRow[] = mount
    .getNavItems()
    .filter((item) => item.kbd !== undefined && item.kbd.length > 0)
    .map((item) => ({ keys: [item.kbd as string], label: `Go to ${item.label}` }));

  const groups: ShortcutGroup[] = [
    { title: 'Surfaces', rows: surfaceRows },
    { title: 'Global', rows: GLOBAL_ROWS },
  ];

  return (
    <div
      className="kbd-help__backdrop"
      role="presentation"
      onClick={() => setOpen(false)}
    >
      <div
        className="kbd-help"
        role="dialog"
        aria-label="Keyboard shortcuts"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kbd-help__head">
          <span className="kbd-help__title">Keyboard shortcuts</span>
          <button
            type="button"
            className="kbd-help__close"
            onClick={() => setOpen(false)}
            aria-label="Close keyboard shortcuts"
          >
            Esc
          </button>
        </div>
        <div className="kbd-help__groups">
          {groups.map((group) => (
            <section key={group.title} className="kbd-help__group">
              <div className="label-mono kbd-help__group-title">{group.title}</div>
              <ul className="kbd-help__list">
                {group.rows.map((row) => (
                  <li key={row.label} className="kbd-help__row">
                    <span className="kbd-help__keys">
                      {row.keys.map((k) => (
                        <kbd key={k} className="kbd-help__kbd">
                          {k}
                        </kbd>
                      ))}
                    </span>
                    <span className="kbd-help__label">{row.label}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
